// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useMemo } from 'react';
import { Dropdown, type DropdownOption } from '@loa/ui-components';
import type { WizardField } from '@loa/fact-find-form';
import type { ProviderSummaryDTO } from '../../types/provider';

interface Props {
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
  providers: ProviderSummaryDTO[];
}

/**
 * Renders a Fact Find field tagged `optionsSource: { type: 'PROVIDERS' }` as a
 * dropdown backed by the brokerage's provider catalogue. The stored value stays
 * a plain provider-name string (the field remains `type: "string"`), so the
 * backend validator is unaffected and any pre-existing free-text value is kept
 * as a selectable option rather than being silently dropped.
 *
 * Uses the shared portalled `Dropdown` (searchable + clearable) — never a native
 * `<select>` — so it matches every other wizard/accordion dropdown. The provider
 * catalogue can be long, hence `search`.
 */
export function ProviderOptionsSelect({ field, value, onChange, testId, providers }: Readonly<Props>) {
  const current = typeof value === 'string' ? value : '';

  const options: DropdownOption[] = useMemo(() => {
    const names = providers.map((p) => p.name).filter(Boolean);
    // Preserve a legacy / off-catalogue value so it still shows + round-trips.
    if (current && !names.includes(current)) {
      names.unshift(current);
    }
    const seen = new Set<string>();
    return names
      .filter((n) => (seen.has(n) ? false : (seen.add(n), true)))
      .map((n) => ({ value: n, text: n }));
  }, [providers, current]);

  return (
    <Dropdown
      label={field.label ?? field.id}
      required={!!field.required}
      fluid
      search
      clearable
      placeholder="Select a provider"
      options={options}
      value={current}
      onChange={(v) => onChange(v === '' ? undefined : v)}
      data-testid={testId}
    />
  );
}
